import dayjs from "dayjs";
import { PageAppRouter } from "@/@types/page";
import dynamic from "next/dynamic";
import Header, { HeaderProps } from "@/templates/header";
import Banner, { BannerProps } from "@/templates/banner";
import SEO, { SEOProps } from "@/templates/seo";
import Description, { DescriptionProps } from "@/templates/descriptions";
import BannerList, { BannerListProps } from "@/templates/homepage/banner-list";
import NewsEvent, { NewsEventProps } from "@/templates/news-event";
import GTag from "@/templates/gtag";
import NotFound from "@/templates/not-found";

//
// Small helpers for building image URLs with graceful fallbacks
// Prefer primary (e.g., mobile) then fallback (e.g., desktop), else empty string
const BASE_STATIC_URL = process.env.NEXT_PUBLIC_BASE_STATIC_URL || "";
const buildUrl = (file?: { url?: string } | null) => (file?.url ? `${BASE_STATIC_URL}${file.url}` : "");
const pickMobileFirst = (
  mobile?: { url?: string } | null,
  desktop?: { url?: string } | null
) => buildUrl(mobile) || buildUrl(desktop);

// ——— Dynamic component definitions ———
//
import { NewsSearchProps } from "@/templates/news-event/search";
const NewsSearch = dynamic<NewsSearchProps>(() =>
  import("@/templates/news-event/search").then((mod) => mod.default)
);

// Dynamically load BannerLogin component
import { BannerProps as BannerLoginProps } from "@/templates/login/banner";
const BannerLogin = dynamic<BannerLoginProps>(() =>
  import("@/templates/login/banner").then((mod) => mod.default)
);

import type { ChatUsProps } from "@/templates/chat-us";
const ChatUs = dynamic<ChatUsProps>(() =>
  import("@/templates/chat-us").then((mod) => mod.default)
);

import type { FooterProps } from "@/templates/footer";
const Footer = dynamic<FooterProps>(() =>
  import("@/templates/footer").then((mod) => mod.default)
);

import type { FacilityHighlightProps } from "@/templates/about-us/facility-highlight";
const FacilityHighlight = dynamic<FacilityHighlightProps>(
  () =>
    import("@/templates/about-us/facility-highlight").then(
      (mod) => mod.default
    ),
  {}
);

import type { LocationNearbyProps } from "@/templates/about-us/location-nearby";
const LocationNearby = dynamic<LocationNearbyProps>(() =>
  import("@/templates/about-us/location-nearby").then((mod) => mod.default)
);

import type { AmenitiesProps as AmenitiesMainProps } from "@/templates/amenities/amenities";
const Amenities = dynamic<AmenitiesMainProps>(() =>
  import("@/templates/amenities/amenities").then((mod) => mod.default)
);

import type { AmenitiesHighlightProps } from "@/templates/amenities/highlight";
const AmenitiesHighlight = dynamic<AmenitiesHighlightProps>(() =>
  import("@/templates/amenities/highlight").then((mod) => mod.default)
);

import type { AmenitiesProps as AmenitiesHomepageProps } from "@/templates/homepage/amenities";
const AmenitiesHomepage = dynamic<AmenitiesHomepageProps>(() =>
  import("@/templates/homepage/amenities").then((mod) => mod.default)
);

import type { GridCol3Props } from "@/templates/homepage/grid-cols-3";
const GridCol3 = dynamic<GridCol3Props>(() =>
  import("@/templates/homepage/grid-cols-3").then((mod) => mod.default)
);

import type { HighlightProps } from "@/templates/homepage/highlight";
const Highlight = dynamic<HighlightProps>(() =>
  import("@/templates/homepage/highlight").then((mod) => mod.default)
);

const KPASimulation = dynamic(() =>
  import("@/templates/homepage/kpa-simulation").then((mod) => mod.default)
);

import type { LocationProps } from "@/templates/homepage/location";
const Location = dynamic<LocationProps>(() =>
  import("@/templates/homepage/location").then((mod) => mod.default)
);

import type { UnitTypeProps } from "@/templates/homepage/unit-type";
const UnitType = dynamic<UnitTypeProps>(() =>
  import("@/templates/homepage/unit-type").then((mod) => mod.default)
);

import type { VirtualTourProps } from "@/templates/homepage/virtual-tour";
const VirtualTour = dynamic<VirtualTourProps>(() =>
  import("@/templates/homepage/virtual-tour").then((mod) => mod.default)
);

import type { NewsEventDetailProps } from "@/templates/news-event/detail";
const NewsEventDetail = dynamic<NewsEventDetailProps>(() =>
  import("@/templates/news-event/detail").then((mod) => mod.default)
);

import type { TnCProps } from "@/templates/tnc/content";
const TnCContent = dynamic<TnCProps>(() =>
  import("@/templates/tnc/content").then((mod) => mod.default)
);

import type { PrivacyProps } from "@/templates/privacy/content";
const PrivacyContent = dynamic<PrivacyProps>(() =>
  import("@/templates/privacy/content").then((mod) => mod.default)
);

import type { SalesBannerProps } from "@/templates/sales/banner";
const SalesBanner = dynamic<SalesBannerProps>(() =>
  import("@/templates/sales/banner").then((mod) => mod.default)
);

const SalesDetail = dynamic(() =>
  import("@/templates/sales/detail").then((mod) => mod.default)
);

import type { SubmissionProps } from "@/templates/submission";
const Submission = dynamic<SubmissionProps>(() =>
  import("@/templates/submission").then((mod) => mod.default)
);

const Comparation = dynamic(() =>
  import("@/templates/unit-type/comparation").then((mod) => mod.default)
);

import type { UnitTypeHighlightProps } from "@/templates/unit-type/highlight";
const UnitTypeHighlight = dynamic<UnitTypeHighlightProps>(() =>
  import("@/templates/unit-type/highlight").then((mod) => mod.default)
);

import type { UnitTypeTowerProps } from "@/templates/unit-type/unit-type-tower";
const UnitTypeTower = dynamic<UnitTypeTowerProps>(() =>
  import("@/templates/unit-type/unit-type-tower").then((mod) => mod.default)
);

import type { CDCProps } from "@/templates/login/cdc";
const CDC = dynamic<CDCProps>(() =>
  import("@/templates/login/cdc").then((mod) => mod.default)
);

import type { ErrorViewProps } from "@/templates/error";

import type { FacilityThumbnailProps } from "@/templates/info-promo/facility-thumbnail";
const FacilityThumbnail = dynamic<FacilityThumbnailProps>(() =>
  import("@/templates/info-promo/facility-thumbnail").then((mod) => mod.default)
);

import type { InfoPromoLocationProps } from "@/templates/info-promo/location";
const InfoPromoLocation = dynamic<InfoPromoLocationProps>(() =>
  import("@/templates/info-promo/location").then((mod) => mod.default)
);

import type { InfoPromoAmenitiesProps } from "@/templates/info-promo/amenities";
const InfoPromoAmenities = dynamic<InfoPromoAmenitiesProps>(() =>
  import("@/templates/info-promo/amenities").then((mod) => mod.default)
);

const ErrorView = dynamic<ErrorViewProps>(() =>
  import("@/templates/error").then((mod) => mod.default)
);

type Props = {
  template: {
    id: string;
    __component: string;
  } & Record<string, any>;
  pathname: string;
} & Partial<PageAppRouter>;

/**
 *
 * @note PLEASE UPDATE THE BUILDER PROPS ALSO WHEN HAS CHANGES IN THIS BUILDER
 * BUILDER PROPS USED FOR SEARCH ALL CONTENT
 * @param args
 * @returns
 */
export default function Builder(args: Props) {
  const { template } = args;

  switch (template.__component) {
    case "shared.menu": {
      const props: HeaderProps = {
        menu: template.menus || [],
      };
      return <Header key={template.id} {...props} />;
    }

    case "homepage.banner-list": {
      const props: BannerListProps = {
        banners: template.banners.map((banner) => {
          return {
            title: banner.title,
            video: buildUrl(banner.video),
            placeholder: buildUrl(banner.placeholder),
          };
        }),

        banners_mobile: template.banners.map((banner) => {
          return {
            title: banner.title,
            video: buildUrl(banner.video),
            placeholder: buildUrl(banner.placeholder),
          };
        }),
      };

      return <BannerList {...props} />;
    }

    case "shared.banner": {
      const props: BannerProps = {
        bannerTiltle: template.title || "",
        bannerUrl: buildUrl(template.thumbnail),
        bannerMobileUrl: pickMobileFirst(template.thumbnail_mobile, template.thumbnail),
      };

      return <Banner {...props} />;
    }

    case "sales.banner": {
      const props: SalesBannerProps = {
        company_logo: buildUrl(template.company_logo),
        thumbnail: buildUrl(template.thumbnail),
        title: template.title,
      };
      return <SalesBanner {...props} />;
    }

    case "shared.description": {
      const props: DescriptionProps = {
        description: template.text,
        background_color: template.background_color,
      };
      return <Description {...props} />;
    }

    case "comparison-page.comparison": {
      return <Comparation />;
    }

    case "homepage.grid-cols-3": {
      const props: GridCol3Props = {
        content: template.content.map((content) => {
          return {
            id: content.id,
            text: content.text,
            thumbnail: buildUrl(content.thumbnail),
            thumbnail_mobile: pickMobileFirst(content.thumbnail_mobile, content.thumbnail),
            icon: buildUrl(content.icon),
          };
        }),
      };
      return <GridCol3 {...props} />;
    }

    case "homepage.highlight": {
      const props: HighlightProps = {
        description: template.description,
        label: template.label,
        thumbnail: buildUrl(template.thumbnail),
        watermark: buildUrl(template.watermark),
      };
      return <Highlight {...props} />;
    }

    case "unit-type.highlight": {
      const props: UnitTypeHighlightProps = {
        description: template.description,
        label: template.label,
        thumbnail: buildUrl(template.thumbnail),
      };
      return <UnitTypeHighlight {...props} />;
    }

    case "amenities.facility-highlight": {
      const props: AmenitiesHighlightProps = {
        description: template.description,
        facility: (template.facility || []).map((facility) => {
          return {
            id: facility.id,
            title: facility.title,
            description: facility.description,
            thumbnail: buildUrl(facility?.thumbnail),
          };
        }),
      };
      return <AmenitiesHighlight {...props} />;
    }

    case "amenities.amenities": {
      const facilities = (template.facilities || []).map(
        ({ amenities_facility: facility, order }) => {
          return {
            ...facility,
            order: order,
            icon: buildUrl(facility?.icon),
          };
        }
      );

      facilities.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

      const props: AmenitiesMainProps = {
        description: template.description,
        facility: facilities,
        facility_thumbnail: (template.facility_thumbnail || []).map((facility) => {
          return {
            ...facility,
            image: buildUrl(facility?.files),
          };
        }),
        title: template.title,
      };
      return <Amenities {...props} />;
    }

    case "unit-type.unit-type-tower": {
      const props: UnitTypeTowerProps = {
        title: template.unit_type_tower.title,
        specifications: template.unit_type_tower.specification.map((spec) => {
          return {
            area: spec.area,
            facility: spec.facility.map((facility) => {
              return {
                id: facility.id,
                label: facility.label,
                icon: buildUrl(facility.icon),
              };
            }),
            id: spec.id,
            image: spec.image.map((image) => buildUrl(image.files)),
            label: spec.label,
            map: buildUrl(spec.map),
            mini_map: buildUrl(spec.mini_map),
            unit: spec.unit,
            short_label: spec.short_label,
            virtual_tour_link: spec.virtual_tour_link,
          } satisfies UnitTypeTowerProps["specifications"]["0"];
        }),
      };

      return <UnitTypeTower {...props} />;
    }

    case "homepage.unit-type": {
      const props: UnitTypeProps = {
        content: template.unit_type_towers.map((tower) => {
          return {
            id: tower.documentId,
            title: tower.title,
            specification: tower.specification.map((spec) => {
              return {
                id: spec.id,
                short_label: spec.short_label,
                unit: spec.unit,
                area: spec.area,
                images: spec.image.map((image) => buildUrl(image.files)),
                facility: spec.facility.map((facility) => ({
                  ...facility,
                  icon: buildUrl(facility.icon),
                })),
                map: buildUrl(spec.map),
                mini_map: buildUrl(spec.mini_map),
              };
            }) satisfies UnitTypeProps["content"]["0"]["specification"],
          };
        }),
      };
      return <UnitType {...props} />;
    }

    case "homepage.virtual-tour": {
      const props: VirtualTourProps = {
        description: template.description,
        title: template.title,
        types: template.types,
      };

      return <VirtualTour {...props} />;
    }

    case "homepage.amenities": {
      const facilities = template.Facilities?.map(
        ({ amenities_facility: facility, order }) => {
          return {
            id: facility.id,
            icon: buildUrl(facility.icon),
            is_new: facility.is_new,
            label: facility.label,
            order: order,
          };
        }
      );
      facilities.sort((a, b) => a.order - b.order);

      const props: AmenitiesHomepageProps = {
        description: template.description,
        title: template.title,
        facility: facilities,
        facility_thumbnail: template.facility_thumbnail.map((facility) => {
          return {
            ...facility,
            image: buildUrl(facility.files),
          };
        }),
      };
      return <AmenitiesHomepage {...props} />;
    }

    case "sales.sales": {
      return <SalesDetail />;
    }

    case "homepage.location": {
      const props: LocationProps = {
        title: template.title,
        description: template.description,
        gmaps_thumbnail: buildUrl(template.gmaps_thumbnail),
        gmaps_link: template.gmaps_link,
        nearby: template.nearby.map((nearby) => {
          return {
            id: nearby.id,
            time: nearby.time,
            icon: buildUrl(nearby.icon),
            location: nearby.location.text,
          };
        }),
      };
      return <Location {...props} />;
    }

    case "homepage.kpa-simulation": {
      return <KPASimulation />;
    }

    case "shared.chat-us": {
      const props: ChatUsProps = {};
      return <ChatUs />;
    }

    case "news-event.news-event": {
      const props: NewsEventProps = {
        social_media_link: template.social_media_link,
        social_media_title: template.social_media_title,
        searchParams: args.searchParams,
        params: args.params,
      };

      return <NewsEvent key={template.id} {...props} />;
    }

    case "about-us.facility-highlight": {
      const props: FacilityHighlightProps = {
        id: template.id,
        company_logo: buildUrl(template.company_logo),
        description: template.description,
        facility: template.facility.map((facility) => {
          return {
            ...facility,
            thumbnail: buildUrl(facility.thumbnail),
          };
        }),
      };

      return <FacilityHighlight {...props} />;
    }

    case "about-us.location": {
      const props: LocationNearbyProps = {
        description: template.description,
        gmaps: template.gmaps,
        gmaps_link: template.gmaps_link,
        id: template.id,
        location_nearby: template.location_nearby.map((location) => {
          return {
            ...location,
            icon: buildUrl(location.icon),
          };
        }),
        text: template.text,
      };
      return <LocationNearby {...props} />;
    }

    case "shared.footer": {
      const props: FooterProps = {
        address: template.footer.address,
        address_gmaps: template.footer.address_gmaps,
        email: template.footer.email,
        email_icon: buildUrl(template.footer.email_icon),
        phone_number: template.footer.phone_number,
        wa_phone_number: template.footer.wa_phone_number,
        wa_phone_icon: buildUrl(template.footer.wa_phone_icon),
        phone_icon: buildUrl(template.footer.phone_icon),
        social_media: template.footer.social_media.map((social) => {
          return {
            id: social.id,
            link: social.link,
            image: buildUrl(social.image),
          };
        }),
        menu: template.footer.menu,
      };
      return <Footer key={template.id} {...props} />;
    }

    case "shared.submission": {
      const props: SubmissionProps = {
        description: template.submission?.description,
        fields: template.submission?.fields || [],
        title: template.submission?.title,
      };
      return <Submission key={template.id} {...props} />;
    }

    case "shared.seo": {
      //render json_ld
      const props: SEOProps = {
        json_ld: template.meta_json_ld,
      };
      return <SEO {...props} />;
    }

    case "privacy.privacy-content": {
      const props: PrivacyProps = {
        content: template.content,
        updatedAt: dayjs(template.updatedAt).format("MMMM D, YYYY"),
      };
      return <PrivacyContent {...props} />;
    }

    case "tnc.privacy-content": {
      const props: TnCProps = {
        content: template.content,
        updatedAt: dayjs(template.updatedAt).format("MMMM D, YYYY"),
      };
      return <TnCContent {...props} />;
    }

    case "news-event.detail": {
      const props: NewsEventDetailProps = {
        content: template.detail.description,
        created_at: template.detail.createdAt,
        custom_date: template.detail.custom_date,
        title: template.detail.title,
        subtitle: template.detail.subtitle,
        slug: template.detail.slug,
        thumbnail: buildUrl(template.detail.images?.[0]),
        category: template.detail.category,
      };

      return <NewsEventDetail {...props} />;
    }

    case "login.banner": {
      const props: BannerLoginProps = {
        bannerUrl: buildUrl(template.image),
      };

      return <BannerLogin {...props} />;
    }

    case "login.cdc": {
      const props: CDCProps = {
        contents: {
          type: "login",
        },
      };
      return <CDC {...props} />;
    }

    case "news-event.search": {
      const props: NewsSearchProps = {};
      return <NewsSearch {...props} />;
    }

    case "script-gtag": {
      return <GTag />;
    }

    case "not-found": {
      return <NotFound />;
    }

    case "error-view": {
      const props: ErrorViewProps = {
        error: template.error ? template.error : Error("Internal Server Error"),
        onRefresh: template.onRefresh ? template.onRefresh : () => {},
      };
      return <ErrorView {...props} />;
    }

    case "info-promo.facility-thumbnail": {
      const props: FacilityThumbnailProps = {
        facility_thumbnail: template.facility_thumbnail.map((facility) => ({
          ...facility,
          image: buildUrl(facility.files),
        })),
      };

      return <FacilityThumbnail {...props} />;
    }

    case "info-promo.location": {
      const props: InfoPromoLocationProps = {
        title: template.title,
        description: template.description,
        nearby: template.nearby.map((nearby) => ({
          id: nearby.id,
          time: nearby.time,
          icon: buildUrl(nearby.icon),
          location: nearby.location.text,
        })),
      };
      return <InfoPromoLocation {...props} />;
    }

    case "info-promo.amenities": {
      const facilities = template.facilities?.map(
        ({ amenities_facility: facility, order }) => {
          return {
            ...facility,
            order: order,
            icon: buildUrl(facility.icon),
          };
        }
      );

      facilities.sort((a, b) => a.order - b.order);

      const props: InfoPromoAmenitiesProps = {
        title: template.title,
        description: template.description,
        facility: facilities,
      };
      return <InfoPromoAmenities {...props} />;
    }

    default: {
      console.log(template.__component + " not being set");
      return null;
    }
  }
}
